import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Inbox, Link2, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

// V5.4 §4 — Candidate Inbox: invited / public / unassigned prospects.
export const CandidateInbox = ({ processes = [] }) => {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [items, setItems] = useState(null);
  const [assigning, setAssigning] = useState({});
  const load = () => api.get('/client/recruitment/inbox').then((r) => setItems(r.data.items)).catch((e) => { toast.error(formatApiError(e)); setItems([]); });
  useEffect(() => { load(); }, []);
  if (!items) return null;

  const screen = async (c, status) => {
    try { await api.post(`/client/recruitment/inbox/${c.id}/screen`, { screening_status: status }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const assign = async (c) => {
    const pid = assigning[c.id];
    if (!pid) return;
    try {
      await api.post(`/client/recruitment/inbox/${c.id}/assign`, { process_id: pid });
      toast.success(L('Candidato asignado al proceso', 'Candidate assigned to process'));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const originBadge = (c) => c.intake === 'public'
    ? <Badge variant="outline" className="text-xs bg-sky-50 text-sky-700 border-sky-200">{L('Vacante pública', 'Public vacancy')}{c.vacancy_title ? ` · ${c.vacancy_title}` : ''}</Badge>
    : c.intake === 'invited'
      ? <Badge variant="outline" className="text-xs bg-teal-50 text-teal-700 border-teal-200">{L('Invitado', 'Invited')}</Badge>
      : <Badge variant="outline" className="text-xs">{L('Prospecto', 'Prospect')}</Badge>;

  return (
    <Card className="p-5 shadow-card" data-testid="candidate-inbox-card">
      <div className="flex items-center gap-2 mb-3">
        <Inbox className="h-4 w-4 text-teal-700" />
        <p className="text-base font-semibold">{L('Bandeja de candidatos', 'Candidate Inbox')}</p>
        <Badge variant="outline" className="text-xs">{items.length}</Badge>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="inbox-empty">
          {L('Sin candidatos entrantes. Las postulaciones públicas y entregas por invitación aparecerán aquí.',
             'No incoming candidates. Public applications and invited submissions will appear here.')}
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <div key={c.id} className="rounded-md border px-3 py-2 text-sm flex items-center gap-2 flex-wrap" data-testid={`inbox-item-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium">{c.name} <span className="text-xs text-muted-foreground">{c.email}</span></p>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  {originBadge(c)}
                  <Badge variant="outline" className="text-xs">{c.screening_status || 'new'}</Badge>
                  <span className="text-xs text-muted-foreground">{c.doc_count} {L('doc(s)', 'doc(s)')}</span>
                  {c.possible_duplicate && (
                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200" data-testid={`inbox-dup-${c.id}`}>
                      {L('Posible duplicado — vinculado al perfil existente', 'Possible duplicate — linked to existing profile')}
                    </Badge>
                  )}
                </div>
              </div>
              <Select onValueChange={(v) => screen(c, v)}>
                <SelectTrigger className="h-8 w-32 text-xs" data-testid={`inbox-screen-${c.id}`}>
                  <SelectValue placeholder={L('Revisión', 'Screening')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reviewing">{L('En revisión', 'Reviewing')}</SelectItem>
                  <SelectItem value="shortlisted">{L('Preseleccionado', 'Shortlisted')}</SelectItem>
                  <SelectItem value="rejected">{L('Rechazado', 'Rejected')}</SelectItem>
                  <SelectItem value="withdrawn">{L('Retirado', 'Withdrawn')}</SelectItem>
                </SelectContent>
              </Select>
              {!c.process_id && (
                <>
                  <Select onValueChange={(v) => setAssigning((p) => ({ ...p, [c.id]: v }))}>
                    <SelectTrigger className="h-8 w-44 text-xs" data-testid={`inbox-process-${c.id}`}>
                      <SelectValue placeholder={L('Proceso…', 'Process…')} />
                    </SelectTrigger>
                    <SelectContent>
                      {processes.map((p) => <SelectItem key={p.id} value={p.id}>{p.position_title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={() => assign(c)} disabled={!assigning[c.id]}
                          data-testid={`inbox-assign-${c.id}`}>
                    <UserCheck className="h-3.5 w-3.5 mr-1" />{L('Asignar', 'Assign')}
                  </Button>
                </>
              )}
              {c.process_id && <Link2 className="h-3.5 w-3.5 text-teal-600" title={L('Asignado a proceso', 'Assigned to process')} />}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
